// ─── Permission slugs (source of truth for the RBAC system) ─────────────────
//
// 'all:all' identifies Super Admin users — they bypass all individual checks.
// These slugs are derived at login time from the raw backend permission objects.

export type PermissionSlug =
  | 'all:all'
  | 'financial:read'
  | 'financial:write'
  | 'collaborators:read'
  | 'collaborators:write'
  | 'logistics:read'
  | 'logistics:write'
  | 'development:read'
  | 'development:write';

// ─── Parser ──────────────────────────────────────────────────────────────────

/**
 * Converts the raw permission objects from the backend (as found in
 * `res.user.loggedUser.permissions`) into a flat array of PermissionSlugs.
 *
 * @example
 * parsePermissionSlugs([{ module: 'all', actions: ['all'] }])
 * // → ['all:all']
 *
 * parsePermissionSlugs([{ module: 'financial', actions: ['read', 'write'] }])
 * // → ['financial:read', 'financial:write']
 */
export const parsePermissionSlugs = (
  rawPermissions: Array<{ module: string; actions: string[] }>,
): PermissionSlug[] => {
  return rawPermissions.flatMap(({ module, actions }) =>
    actions.map((action) => `${module}:${action}` as PermissionSlug),
  );
};
